import axios from "axios";
import { getLatestDocVersions } from "./tenants";
import { setServerConfig, markServerConfigLoaded } from "./apiClient";
import { TENANT_BROKERAGE_MAP, BROKERAGE_TRADOVATE } from "./constants";
import { getOptionsTier as getOptionsTierFor, getMaxOptionsAccounts as getMaxOptionsAccountsFor } from "./optionsLimits";

let userAccess;

export const getUserPermissions = async (token, userId) => {
    const headers = {
        'Authorization': `Bearer ${token}`,
    };
    const params = {
        uid: userId
    };
    let urlPath = '/api/v2/permissions';
    if (userId) {
        try {
            if (!userAccess) {
                const permissionResp = await axios.get(
                    `${process.env.REACT_APP_SERVERLESS_API_URL}${urlPath}`,
                    {
                        headers: headers,
                        params: params,
                    }
                );
                if (permissionResp && permissionResp.data) {
                    userAccess = permissionResp.data;
                    if (userAccess.serverName) {
                        setServerConfig({
                            serverName: userAccess.serverName,
                            alb: userAccess.alb,
                        });
                    }
                }
            }
            return userAccess;
        } catch (error) {
            console.log(error.message);
            return error.message;
        } finally {
            // Unblock requests waiting on serverConfig, even if the user has no server or this failed
            markServerConfigLoaded();
        }
    }
}

export const isAuthorized = (appControlBeingAccessed) => {
    if (
        userAccess && 
        userAccess.permissions && 
        userAccess.permissions.length
    ) {
        if (userAccess.permissions.includes('trade_eval_accounts')) {
            if (
                appControlBeingAccessed === 'connectionScreen' ||
                appControlBeingAccessed === 'tradeScreen' ||
                appControlBeingAccessed === 'tradeEvaluationAccount'
            ) {
                return true;
            }
        }
        if (userAccess.permissions.includes('trade_paid_accounts')) {
            if (appControlBeingAccessed === 'tradePaidAccount') {
                return true;
            }
        } 
        if (userAccess.permissions.includes('trade_live_accounts')) {
            if (appControlBeingAccessed === 'tradeLiveAccount') {
                return true;
            }
        }
        if (userAccess.permissions.includes('admin')) {
            if (appControlBeingAccessed === 'admin') {
                return true;
            }
        }
        if (userAccess.permissions.includes('metricsAdmin')) {
            if (appControlBeingAccessed === 'metricsAdmin') {
                return true;
            }
        }
    }
    return false;
}

export const getUserTenants = () => {
    return userAccess?.tenants || [];
}

// The signed-in user's options access (tier + account limit), read from the
// cached permissions. Rules live in optionsLimits.js.
export const getUserAccess = () => userAccess;
export const getOptionsTier = () => getOptionsTierFor(userAccess);
export const getMaxOptionsAccounts = () => getMaxOptionsAccountsFor(userAccess);

// Lowercase algo slugs the user is entitled to (e.g. 'marketpower', 'krypton').
export const getUserAlgorithms = () => {
    return (userAccess?.algorithms || []).map((a) => String(a).toLowerCase());
}

export const hasTenant = (tenantName) => {
    const tenants = getUserTenants();
    if (tenants.length === 0) return tenantName === 'investceo';
    return tenants.includes(tenantName);
}

export const getAllowedBrokerages = () => {
    const tenants = getUserTenants();
    if (tenants.length === 0) return [BROKERAGE_TRADOVATE];
    const brokerages = new Set();
    tenants.forEach((tenant) => {
        (TENANT_BROKERAGE_MAP[tenant] || []).forEach((b) => brokerages.add(b));
    });
    return [...brokerages];
}

// Check if user has signed latest T&C for ALL of their tenants
export const hasSignedLatestTermsAndConditions = (permissions) => {
    if (!permissions || !permissions.permissions || !permissions.permissions.length) {
        return false;
    }

    const unsignedTenants = getUnsignedTenants(permissions);
    return unsignedTenants.length === 0;
}

// Returns list of tenants the user has NOT yet signed latest T&C for
export const getUnsignedTenants = (permissions) => {
    const tenants = (permissions && permissions.tenants && permissions.tenants.length)
        ? permissions.tenants
        : ['investceo'];
    const documents = (permissions && permissions.documents) ? permissions.documents : [];

    const unsigned = [];
    for (const tenant of tenants) {
        const { termsAndConditionsVersion, privacyPolicyVersion } = getLatestDocVersions(tenant);
        const signed = documents.find((doc) => {
            const docTenant = doc.tenant || 'investceo';
            return docTenant === tenant &&
                doc.termsAndConditionsVersion === termsAndConditionsVersion &&
                doc.privacyPolicyVersion === privacyPolicyVersion;
        });
        if (!signed) {
            unsigned.push(tenant);
        }
    }
    return unsigned;
}